import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { connectedForBoo, hubAccountsKey } from "@/lib/hub-accounts";
import { useBooCanConfigure } from "@/lib/use-boo-configurable";
import { CatalogTile } from "@/pages/container-tabs/integrations/HubApps";
import { useConnectApp } from "@/pages/container-tabs/integrations/hub-lib";
import { useQuery } from "@tanstack/react-query";
import { beginCatalogConnect, endCatalogConnect } from "../catalog-activity";
import { CatalogTakeover, PROVISIONING_RETURN_TO } from "../ConnectAppsInline";
import { COS_APPS, connectedKinds, type CosApp } from "./apps";
import { NeedDots } from "./pieces";

// The chief-of-staff replacement for the teammate "connect 3+ integrations"
// pitch: the four hub apps a chief of staff runs on, connected through the
// same hub flow the catalog uses. Accounts connect private to the Boo being
// set up (containerId) exactly like the catalog during onboarding, so they
// are usable the moment the Boo is ready and nobody else's Boo sees them.
// Until the Boo's id is known the card waits (the way forward still shows).

// Resolve the four slugs against the live catalog so names, blurbs, and logos
// are the hub's own; a slug the hub does not know keeps the static entry.
async function resolveCosApps(): Promise<CosApp[]> {
  return Promise.all(
    COS_APPS.map(async (fallback) => {
      const page = await api.integrations.apps({ q: fallback.name, limit: 10 }).catch(() => null);
      const found = page?.apps.find((app) => app.slug === fallback.slug);
      return found ? { ...found, kind: fallback.kind } : fallback;
    }),
  );
}

export function CosInboxPitch({
  booName,
  containerId,
  ready,
  onContinue,
}: {
  booName: string;
  containerId?: string;
  ready: boolean;
  onContinue: () => void;
}) {

  const accountsQuery = useQuery({
    queryKey: hubAccountsKey(containerId),
    queryFn: () => api.integrations.accounts(containerId),
    enabled: !!containerId,
    retry: false,
  });
  const canConnect = useBooCanConfigure(containerId);
  const appsQuery = useQuery({
    queryKey: ["cos-hub-apps"],
    queryFn: resolveCosApps,
    staleTime: Infinity,
    retry: false,
  });
  const { connect: startConnect, connecting } = useConnectApp({
    containerId,
    returnTo: PROVISIONING_RETURN_TO,
  });
  // Report connect flights to the shared activity store so the provisioning
  // step never auto-advances mid-consent (same contract as the catalog).
  const connect = async (app: CosApp) => {
    beginCatalogConnect();
    try {
      await startConnect(app);
    } finally {
      endCatalogConnect();
    }
  };

  const connectedSlugs = containerId
    ? connectedForBoo(accountsQuery.data?.accounts ?? [], containerId).map((a) => a.app)
    : [];
  const kinds = connectedKinds(connectedSlugs);
  const complete = kinds.size >= 2;

  // No Boo yet, hub unreachable, or the user can't configure the Boo (the Boo
  // permission, not an org role): the wait is the job of this screen, so drop
  // the card and keep only the way forward.
  const gated = !containerId || accountsQuery.isError || canConnect === false;

  const apps = appsQuery.data ?? COS_APPS;

  return (
    <>
      {!gated && (
        <div className="space-y-3 rounded-lg border p-4 text-left">
          <div className="space-y-1">
            <h2 className="text-sm font-semibold">Connect your inbox and calendar</h2>
            <p className="text-sm text-muted-foreground">
              {booName} drafts replies and books time for you. Nothing goes out
              without your OK.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <NeedDots kinds={kinds} />
            <span className="pl-1 text-xs text-muted-foreground tabular-nums">
              {complete ? "Both connected" : `${kinds.size} of 2 connected`}
            </span>
          </div>
          {/* Tighter than the catalog's own padding: four tiles plus the
              headers have to fit a laptop viewport without scrolling. */}
          <div className="flex flex-col gap-1.5 [&>button]:p-2.5">
            {apps.map((app) => (
              <CatalogTile
                key={app.slug}
                app={app}
                connectedCount={connectedSlugs.filter((slug) => slug === app.slug).length}
                busy={connecting === app.slug}
                disabled={connecting !== null}
                onConnect={() => void connect(app)}
              />
            ))}
          </div>
          <CatalogTakeover trigger="link" containerId={containerId} />
        </div>
      )}

      {ready &&
        (complete ? (
          <Button className="w-full" onClick={onContinue}>
            Continue
          </Button>
        ) : (
          <button
            type="button"
            onClick={onContinue}
            className="text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            {gated ? "Continue" : "Skip for now"}
          </button>
        ))}
    </>
  );
}
